import { get } from "lodash";
import { unparse as convertToCSV } from "papaparse/papaparse.min";
import downloadCSV from "../../../../utils/downloadCSV";
import parseLink from "../../parseLink";
import { getEnv } from "../../../../config";

const ID_TABLE_CASA_DI_VETRO = "casa-di-vetro";
const ID_COMMON_TABLE = "common-table";

const responsiveTableWrap = {
  width: "100%",
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "thin",
};

const tableBase = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const modernTableStyles = {
  mainContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    minHeight: "100vh",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    background: "white",
    borderRadius: "12px",
    boxShadow:
      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    marginBottom: "1.5rem",
  },
  cardHeader: {
    padding: "1rem",
    borderBottom: "1px solid #e5e7eb",
    background: "linear-gradient(90deg, rgb(9 46 116), #5681b6)",
  },
  cardBody: {
    padding: "1.5rem",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "linear-gradient(90deg, rgb(9 46 116), #5681b6)",
    borderBottom: "1px solid #e5e7eb",
  },
  noResultsContainer: {
    textAlign: "center",
    padding: "3rem 1.5rem",
    color: "#6b7280",
  },
  noResultsText: {
    fontSize: "1rem",
    fontWeight: "500",
    marginBottom: "0.5rem",
  },
  noResultsSubtext: {
    fontSize: "0.875rem",
    color: "#9ca3af",
  },
  exportButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1rem",
    background: "#1e40af",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: "1.5rem",
  },
  filtersToggle: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "none",
    border: "none",
    color: "white",
    fontWeight: "700",
    cursor: "pointer",
    transition: "color 0.2s ease",
    fontSize: "15.75px",
    padding: 0,
    width: "100%",
    textAlign: "left",
  },
  paginationInfo: {
    fontSize: "0.875rem",
    color: "#6b7280",
    padding: "1.5rem",
    borderTop: "1px solid #e5e7eb",
  },
};
const INVALID_LINKS = ["#", "https://#", "http://#"];

const formatters = {
  isValidLink: (link) => {
    return link && !INVALID_LINKS.includes(link);
  },

  formatTextCell: (cell) => {
    const value = get(cell, "raw_value", "");
    if (value === null || value === undefined || value === "") return "";
    const doc = new DOMParser().parseFromString(String(value), "text/html");
    return (doc.body.textContent ?? "").trim();
  },

  formatDateCell: (cell) => {
    const date = get(cell, "raw_value", "");
    return date ? new Date(date).toISOString().split("T")[0] : "";
  },

  formatLinkCell: (cell) => {
    const link = get(cell, "link", null);
    if (!link) return "";

    if (typeof link === "string" && formatters.isValidLink(link)) {
      return link;
    }
    if (
      typeof link === "object" &&
      link?.url &&
      formatters.isValidLink(link.url)
    ) {
      return link.url;
    }

    return "";
  },

  formatLinksCell: (cell) => {
    const links = get(cell, "links", []);
    if (!Array.isArray(links) || links.length === 0) return "";

    return links
      .map((link) => {
        if (!link) return "";

        if (typeof link === "string" && formatters.isValidLink(link)) {
          return parseLink ? parseLink(link) : link;
        }
        if (
          typeof link === "object" &&
          link.url &&
          formatters.isValidLink(link.url)
        ) {
          return parseLink ? parseLink(link.url) : link.url;
        }

        return "";
      })
      .filter(Boolean)
      .join("\n");
  },

  formatDocCell: (cell) => {
    const doc = get(cell, "doc", null);
    if (!doc) return "";

    if (typeof doc === "string" && formatters.isValidLink(doc)) {
      return doc;
    }
    if (typeof doc === "object" && doc.url && formatters.isValidLink(doc.url)) {
      return parseLink ? parseLink(doc.url) : doc.url;
    }

    return "";
  },

  formatDocsCell: (cell) => {
    const docs = get(cell, "docs", []);
    if (!Array.isArray(docs) || docs.length === 0) return "";

    return docs
      .map((doc) => {
        if (!doc) return "";

        if (typeof doc === "string" && formatters.isValidLink(doc)) {
          return parseLink ? parseLink(doc) : doc;
        }
        if (
          typeof doc === "object" &&
          doc.url &&
          formatters.isValidLink(doc.url)
        ) {
          return parseLink ? parseLink(doc.url) : doc.url;
        }

        return "";
      })
      .filter(Boolean)
      .join("\n");
  },

  formatComplexTypes: (cell, defType) => {
    switch (defType) {
      case "one_link":
        return formatters.formatLinkCell(cell);
      case "many_links":
        return formatters.formatLinksCell(cell);
      case "one_doc":
        return formatters.formatDocCell(cell);
      case "many_docs":
        return formatters.formatDocsCell(cell);
      default:
        return "";
    }
  },

  formatCellValue: (cell, column) => {
    const defType = get(column, "def_type");
    switch (defType) {
      case "text":
      case "autocomplete":
      case "number":
      case "email":
        return formatters.formatTextCell(cell);
      case "date":
      case "dateSinglePicker":
      case "dateCasaDiVetro":
        return formatters.formatDateCell(cell);
      default:
        return formatters.formatComplexTypes(cell, defType);
    }
  },
};

const exportTable = async (path, params, onError, dataProvider, idTable) => {
  try {
    let result,
      contents = [];

    // Funzione per esportare i dati in CSV
    const exportCSV = (headers, rows, filename = "export") => {
      const headerNames = headers.map((col) => get(col, "name"));
      const dataCSV = rows.map((row) =>
        headers.map((col) =>
          formatters.formatCellValue(
            get(row, `[${get(col, "id") || get(col, "name")}]`),
            col,
          ),
        ),
      );
      const csv = convertToCSV([headerNames, ...dataCSV], {
        fields: headerNames,
        delimiter: ";",
      });

      if (typeof downloadCSV === "function") {
        downloadCSV(csv, filename);
      } else {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    if (idTable === ID_TABLE_CASA_DI_VETRO) {
      const { filter, transformApiDataToTable } = params;
      const BASE_URL = getEnv("REACT_APP_CASA_DI_VETRO_BASE_URL");
      const PATH_PDF = `casadivetro/api/sinfonia/downloadAtto`;
      const PAGE_SIZE = 2000;
      const firstResult = await dataProvider.getCasaDiVetroList(path, {
        ...filter,
        page: 0,
        size: PAGE_SIZE,
      });

      const totalElements = firstResult?.data?.totalElements || 0;
      const firstContent = firstResult?.data?.content || [];

      if (!totalElements || firstContent.length === 0) {
        alert("Nessun dato da esportare.");
        return;
      }
      const totalPages = Math.ceil(totalElements / PAGE_SIZE);
      const remainingRequests = [];
      for (let page = 1; page < totalPages; page++) {
        remainingRequests.push(
          dataProvider.getCasaDiVetroList(path, {
            ...filter,
            page,
            size: PAGE_SIZE,
          }),
        );
      }

      const remainingResults = await Promise.all(remainingRequests);
      const remainingContent = remainingResults.flatMap(
        (r) => r?.data?.content || [],
      );

      contents = [...firstContent, ...remainingContent];

      if (!contents.length) {
        alert("Nessun dato da esportare.");
        return;
      }
      result = {
        data: {
          ...firstResult.data,
          content: contents,
          totalElements,
        },
      };

      const { headers, rows } = transformApiDataToTable(result.data);
      rows.forEach((row) => {
        const dataAtto = row.dataAtto?.raw_value;

        let year = new Date().getFullYear();
        if (typeof dataAtto === "string") {
          const yearAtto = Number(dataAtto.split("/")?.[2]);
          if (!isNaN(yearAtto)) year = yearAtto;
        }

        headers.forEach((col) => {
          const colId = get(col, "id");
          const colName = get(col, "name");

          const cell = row[colId] ?? row[colName];
          const rawValue = cell?.raw_value;

          if (
            typeof rawValue === "string" &&
            rawValue.toLowerCase().endsWith(".pdf")
          ) {
            const newValue =
              `${BASE_URL}/${PATH_PDF}` +
              `?fileName=${encodeURIComponent(rawValue)}` +
              `&anno=${year}`;

            if (row[colId]) {
              row[colId] = { ...cell, raw_value: newValue };
            }
            if (row[colName]) {
              row[colName] = { ...cell, raw_value: newValue };
            }
          }
        });
      });
      exportCSV(headers, rows);
    } else {
      const { uuid, filter, sort } = params;
      result = await dataProvider.getPost(path, {
        method: "nodePage",
        uuid,
        filter,
        limit: null,
        offset: null,
        sort,
      });
      contents = result?.data?.contents || [];

      if (!contents.length) {
        alert("Nessun dato da esportare.");
        return;
      }

      const rows = contents.flatMap((item) => get(item, "table.rows", []));
      const headers = get(contents, "[0].table.headers", []);
      exportCSV(headers, rows);
    }
  } catch (err) {
    console.error("Errore durante l'esportazione CSV:", err);
    onError("Errore durante l'esportazione");
  }
};

export const useSharedTableComponent = () => {
  return {
    exportTable,
    modernTableStyles,
    ID_TABLE_CASA_DI_VETRO,
    ID_COMMON_TABLE,
    responsiveTableWrap,
    tableBase,
  };
};
